import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTts } from '../../contexts/TtsContext';
import { Play, Pause, X, Volume2, VolumeX, ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { useTranslation } from '../../i18n/useTranslation';

/**
 * Compact TTS Player with Inline Controls
 * - Floating and draggable
 * - Chunk-based progress slider (seek to any chunk)
 * - Inline voice, speed, and volume controls (no modal)
 * - Default position: bottom-right corner (100px from edges)
 */
export const CompactTTSPlayer: React.FC = () => {
  const { t } = useTranslation();
  const {
    isActive,
    playbackState,
    currentChunkIndex,
    totalChunks,
    currentTime,
    duration,
    settings,
    availableVoices,
    play,
    pause,
    deactivateTts,
    seekToChunk,
    updateSettings,
  } = useTts();

  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showSpeedSlider, setShowSpeedSlider] = useState(false);

  // Refs for click-outside detection
  const speedSliderRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);

  // Dragging state
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    // Load saved position from localStorage or use default (bottom-right)
    const saved = localStorage.getItem('tts-player-position');
    if (saved) {
      return JSON.parse(saved);
    }
    return { bottom: 100, right: 100 };
  });
  const dragStartPos = useRef({ x: 0, y: 0 });
  const playerRef = useRef<HTMLDivElement>(null);

  // Save position to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('tts-player-position', JSON.stringify(position));
  }, [position]);

  // Drag event handlers (mouse)
  const handleMouseDown = (e: React.MouseEvent) => {
    // Only allow dragging from header or grip area
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('select') || target.closest('input')) {
      // Don't drag if clicking interactive elements
      return;
    }
    setIsDragging(true);
    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
  };

  // Touch event handlers (mobile)
  const handleTouchStart = (e: React.TouchEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('select') || target.closest('input')) {
      return;
    }
    const touch = e.touches[0];
    setIsDragging(true);
    dragStartPos.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || e.touches.length === 0) return;
    e.preventDefault(); // Prevent scrolling while dragging

    const touch = e.touches[0];
    const deltaX = dragStartPos.current.x - touch.clientX;
    const deltaY = dragStartPos.current.y - touch.clientY; // Fixed: inverted to match bottom positioning

    setPosition(prev => ({
      bottom: Math.max(10, Math.min(window.innerHeight - 150, prev.bottom + deltaY)),
      right: Math.max(10, Math.min(window.innerWidth - 300, prev.right + deltaX))
    }));

    dragStartPos.current = {
      x: touch.clientX,
      y: touch.clientY
    };
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = dragStartPos.current.x - e.clientX;
    const deltaY = dragStartPos.current.y - e.clientY; // Fixed: inverted to match bottom positioning

    setPosition(prev => ({
      bottom: Math.max(10, Math.min(window.innerHeight - 150, prev.bottom + deltaY)),
      right: Math.max(10, Math.min(window.innerWidth - 300, prev.right + deltaX))
    }));

    dragStartPos.current = {
      x: e.clientX,
      y: e.clientY
    };
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse and touch event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Click-outside handler for speed and volume sliders
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showSpeedSlider && speedSliderRef.current && !speedSliderRef.current.contains(event.target as Node)) {
        setShowSpeedSlider(false);
      }
      if (showVolumeSlider && volumeSliderRef.current && !volumeSliderRef.current.contains(event.target as Node)) {
        setShowVolumeSlider(false);
      }
    };

    if (showSpeedSlider || showVolumeSlider) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showSpeedSlider, showVolumeSlider]);

  if (!isActive) {
    return null;
  }

  const isPlaying = playbackState === 'playing';
  const isLoading = playbackState === 'loading';

  // Chunk progress (0-100%)
  const chunkProgress = totalChunks > 0 ? ((currentChunkIndex + 1) / totalChunks) * 100 : 0;

  const handleChunkSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newChunkIndex = parseInt(e.target.value, 10);
    seekToChunk(newChunkIndex);
  };

  const handleSpeedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSpeed = parseFloat(e.target.value);
    updateSettings({ speed: newSpeed });
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    updateSettings({ volume: newVolume });
  };

  return (
    <div
      ref={playerRef}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      className={`fixed z-50 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-2xl transition-shadow ${
        isDragging ? 'cursor-grabbing shadow-3xl' : 'cursor-grab'
      }`}
      style={{
        width: '420px',
        maxWidth: 'calc(100vw - 20px)',
        bottom: `${position.bottom}px`,
        right: `${position.right}px`,
        userSelect: isDragging ? 'none' : 'auto',
        touchAction: 'none', // Prevent default touch behaviors while dragging
      }}
      role="region"
      aria-label={t('App.Reader.TTS.CompactTitle')}
    >
      <div className="px-4 py-3">
        {/* Header Row with Drag Handle */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-neutral-400 dark:text-neutral-500 cursor-grab" />
            <Volume2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              {t('App.Reader.TTS.CompactTitle')}
            </span>
          </div>
          <button
            onClick={deactivateTts}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            aria-label={t('App.Reader.TTS.CompactStopLabel')}
          >
            <X className="w-4 h-4 text-neutral-600 dark:text-neutral-400" />
          </button>
        </div>

        {/* Main Controls Row */}
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 sm:gap-4 items-center">
          {/* Left: Voice Selection */}
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            <label htmlFor="tts-voice-select" className="text-xs text-neutral-600 dark:text-neutral-400 whitespace-nowrap hidden sm:inline">
              {t('App.Reader.TTS.VoiceLabel')}:
            </label>
            <select
              id="tts-voice-select"
              value={settings.voiceId}
              onChange={(e) => updateSettings({ voiceId: e.target.value })}
              className="flex-1 min-w-0 px-2 py-1.5 text-xs sm:text-sm bg-white dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:text-neutral-100 truncate"
            >
              {availableVoices.map(voice => (
                <option key={voice.voiceId} value={voice.voiceId}>
                  {voice.displayName}
                </option>
              ))}
            </select>
          </div>

          {/* Center: Playback Controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => seekToChunk(Math.max(0, currentChunkIndex - 1))}
              disabled={currentChunkIndex === 0 || isLoading}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label={t('App.Reader.TTS.PreviousLabel')}
            >
              <ChevronLeft className="w-5 h-5 text-neutral-700 dark:text-neutral-200" />
            </button>

            <button
              onClick={isPlaying ? pause : play}
              disabled={isLoading}
              className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 disabled:cursor-not-allowed transition-colors shadow-lg"
              aria-label={isPlaying ? t('App.Reader.TTS.PauseLabel') : t('App.Reader.TTS.PlayLabel')}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : isPlaying ? (
                <Pause className="w-5 h-5 text-white fill-white" />
              ) : (
                <Play className="w-5 h-5 text-white fill-white ml-0.5" />
              )}
            </button>

            <button
              onClick={() => seekToChunk(Math.min(totalChunks - 1, currentChunkIndex + 1))}
              disabled={currentChunkIndex >= totalChunks - 1 || isLoading}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label={t('App.Reader.TTS.NextLabel')}
            >
              <ChevronRight className="w-5 h-5 text-neutral-700 dark:text-neutral-200" />
            </button>
          </div>

          {/* Right: Speed & Volume */}
          <div className="flex items-center justify-end gap-3">
            {/* Speed Control */}
            <div className="relative" ref={speedSliderRef}>
              <button
                onClick={() => setShowSpeedSlider(!showSpeedSlider)}
                className="px-3 py-1.5 text-sm font-medium bg-neutral-100 dark:bg-neutral-700 hover:bg-neutral-200 dark:hover:bg-neutral-600 rounded-lg transition-colors"
                aria-label={t('App.Reader.TTS.SpeedButtonLabel')}
              >
                {t('App.Reader.TTS.SpeedButtonValue', { speed: settings.speed })}
              </button>

              {/* Speed Slider Popup */}
              {showSpeedSlider && (
                <div
                  className="absolute bottom-full right-0 mb-2 p-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-xl"
                >
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-xs text-neutral-600 dark:text-neutral-400">
                      {settings.speed}×
                    </span>
                    <input
                      type="range"
                      min="0.5"
                      max="2.0"
                      step="0.25"
                      value={settings.speed}
                      onChange={handleSpeedChange}
                      className="h-24 w-2 appearance-none rounded-lg cursor-pointer [writing-mode:vertical-lr] [direction:rtl]
                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg
                        [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-lg"
                      style={{
                        background: `linear-gradient(to top, rgb(var(--color-primary-600)) ${((settings.speed - 0.5) / 1.5) * 100}%, rgb(var(--color-neutral-200)) ${((settings.speed - 0.5) / 1.5) * 100}%)`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Volume Control */}
            <div className="relative" ref={volumeSliderRef}>
              <button
                onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                aria-label={t('App.Reader.TTS.VolumeButtonLabel')}
              >
                {settings.volume === 0 ? (
                  <VolumeX className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                ) : (
                  <Volume2 className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                )}
              </button>

              {/* Volume Slider Popup */}
              {showVolumeSlider && (
                <div
                  className="absolute bottom-full right-0 mb-2 p-3 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-xl"
                >
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-xs text-neutral-600 dark:text-neutral-400">
                      {Math.round(settings.volume * 100)}%
                    </span>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={settings.volume}
                      onChange={handleVolumeChange}
                      className="h-24 w-2 appearance-none rounded-lg cursor-pointer [writing-mode:vertical-lr] [direction:rtl]
                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg
                        [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-lg"
                      style={{
                        background: `linear-gradient(to top, rgb(var(--color-primary-600)) ${settings.volume * 100}%, rgb(var(--color-neutral-200)) ${settings.volume * 100}%)`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Chunk Progress Slider */}
        <div className="mt-3">
          <input
            type="range"
            min="0"
            max={Math.max(0, totalChunks - 1)}
            value={currentChunkIndex}
            onChange={handleChunkSliderChange}
            className="w-full h-2 appearance-none bg-neutral-200 dark:bg-neutral-700 rounded-full cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-lg
              [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:shadow-lg"
            style={{
              background: `linear-gradient(to right, rgb(var(--color-primary-600)) ${chunkProgress}%, rgb(var(--color-neutral-200)) ${chunkProgress}%)`
            }}
            aria-label={t('App.Reader.TTS.SeekAriaLabel', { current: currentChunkIndex + 1, total: totalChunks })}
            aria-valuetext={t('App.Reader.TTS.ChunkIndicator', { current: currentChunkIndex + 1, total: totalChunks })}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, totalChunks - 1)}
            aria-valuenow={currentChunkIndex}
          />
        </div>

        {/* Hidden time-based progress (kept for future use with reading position) */}
        <input
          type="range"
          min="0"
          max={duration || 0}
          value={currentTime}
          className="hidden"
          disabled
          aria-hidden="true"
        />
      </div>
    </div>
  );
};
